/**
 * Debug logging utility for TickerPriceChart.
 * Provides a centralized logging mechanism that can be enabled/disabled.
 */

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface ChartLogger {
  debug: (message: string, ...args: unknown[]) => void;
  info: (message: string, ...args: unknown[]) => void;
  warn: (message: string, ...args: unknown[]) => void;
  error: (message: string, ...args: unknown[]) => void;
  setEnabled: (enabled: boolean) => void;
  isEnabled: () => boolean;
}

/**
 * Create a logger instance for chart debugging.
 * @param prefix - Prefix for all log messages (e.g., component name)
 * @param enabled - Initial enabled state
 */
export function createChartLogger(
  prefix: string,
  enabled = false
): ChartLogger {
  let isDebugEnabled = enabled;

  const formatMessage = (level: LogLevel, message: string): string => {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, "0");
    const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
      now.getDate()
    )} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(
      now.getSeconds()
    )}`;

    return `[${timestamp}] [${level.toUpperCase()}] [${prefix}] ${message}`;
  };

  const log = (level: LogLevel, message: string, ...args: unknown[]): void => {
    if (!isDebugEnabled && level === "debug") {
      return;
    }

    const formattedMessage = formatMessage(level, message);

    switch (level) {
      case "debug":
      case "info":
        // eslint-disable-next-line no-console
        console.log(formattedMessage, ...args);
        break;
      case "warn":
        // eslint-disable-next-line no-console
        console.warn(formattedMessage, ...args);
        break;
      case "error":
        // eslint-disable-next-line no-console
        console.error(formattedMessage, ...args);
        break;
    }
  };

  return {
    debug: (message: string, ...args: unknown[]) =>
      log("debug", message, ...args),
    info: (message: string, ...args: unknown[]) =>
      log("info", message, ...args),
    warn: (message: string, ...args: unknown[]) =>
      log("warn", message, ...args),
    error: (message: string, ...args: unknown[]) =>
      log("error", message, ...args),
    setEnabled: (enable: boolean) => {
      isDebugEnabled = enable;
    },
    isEnabled: () => isDebugEnabled,
  };
}

/**
 * Global chart logger instance.
 * Use this for logging across all chart components.
 */
let globalLogger: ChartLogger | null = null;

/**
 * Get or create the global chart logger.
 */
export function getChartLogger(): ChartLogger {
  globalLogger ??= createChartLogger("TickerPriceChart", false);

  return globalLogger;
}

/**
 * Set the global debug state for all chart logging.
 */
export function setChartDebug(enabled: boolean): void {
  getChartLogger().setEnabled(enabled);
}

/**
 * Check if chart debugging is enabled.
 */
export function isChartDebugEnabled(): boolean {
  return getChartLogger().isEnabled();
}

/**
 * Reset the global logger singleton.
 * Only available in test environment to prevent cross-test pollution.
 */
export function resetChartLogger(): void {
  if (process.env.NODE_ENV === "test") {
    globalLogger = null;
  }
}
